import { useEffect, useRef, useState } from 'react';
import { api, type SchoolSearchResult } from '../api';
import { SchoolAvatar } from './SchoolAvatar';
import type { PledgeTarget } from './PledgePanel';

interface Props {
  conferenceName: (id: string) => string;
  onSelect: (target: PledgeTarget) => void;
}

export function SchoolSearch({ conferenceName, onSelect }: Props) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState<SchoolSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  // "My school is missing" suggestion state.
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestNote, setSuggestNote] = useState('');
  const [suggestState, setSuggestState] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounce.current = setTimeout(async () => {
      try {
        const { results } = await api.searchSchools(term);
        setResults(results);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [q]);

  // Reset the suggestion form whenever the query changes.
  useEffect(() => {
    setSuggestOpen(false);
    setSuggestNote('');
    setSuggestState('idle');
  }, [q]);

  async function submitSuggestion() {
    const name = q.trim();
    if (!name) return;
    setSuggestState('sending');
    try {
      await api.suggestSchool({ name, note: suggestNote.trim() || undefined });
      setSuggestState('sent');
    } catch {
      setSuggestState('error');
    }
  }

  return (
    <section className="finder">
      <h2 className="finder__title">Find your school</h2>
      <input
        className="finder__input"
        aria-label="Search schools"
        placeholder="Type a college or university…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        autoComplete="off"
      />
      {q.trim().length >= 2 && (
        <ul className="finder__results" aria-label="Search results">
          {loading && <li className="finder__hint">Searching…</li>}
          {!loading && results.length === 0 && (
            <li className="finder__hint finder__hint--missing">
              <span>No schools match “{q.trim()}”.</span>
              {suggestState === 'sent' ? (
                <span className="finder__suggest-done">
                  ✓ Thanks — we’ll review “{q.trim()}” for the roster.
                </span>
              ) : suggestOpen ? (
                <div className="finder__suggest">
                  <label className="finder__suggest-label" htmlFor="suggest-note">
                    Suggest <strong>{q.trim()}</strong> (optional: conference, division, or state)
                  </label>
                  <textarea
                    id="suggest-note"
                    className="finder__suggest-note"
                    placeholder="e.g. NAIA, Sooner Athletic Conference, Oklahoma"
                    value={suggestNote}
                    maxLength={500}
                    rows={2}
                    onChange={(e) => setSuggestNote(e.target.value)}
                  />
                  <div className="finder__suggest-actions">
                    <button
                      className="finder__suggest-submit"
                      disabled={suggestState === 'sending'}
                      onClick={submitSuggestion}
                    >
                      {suggestState === 'sending' ? 'Sending…' : 'Submit suggestion'}
                    </button>
                    {suggestState === 'error' && (
                      <span className="finder__suggest-error">Couldn’t send — try again.</span>
                    )}
                  </div>
                </div>
              ) : (
                <button className="finder__suggest-open" onClick={() => setSuggestOpen(true)}>
                  Don’t see your school? Suggest it →
                </button>
              )}
            </li>
          )}
          {results.map((s) => (
            <li key={s.id}>
              <button className="finder__result" onClick={() => onSelect(s)}>
                <SchoolAvatar name={s.name} primary={s.primary} secondary={s.secondary} size={28} />
                <span className="finder__result-name">{s.name}</span>
                <span className="chip">{conferenceName(s.conferenceId)}</span>
                <span className="chip chip--div">{s.division}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
